import { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import CrmCellEditor from "components/_dashboard/crm/table/edit/CrmCellEditor";
import {
  announceCrmCellEdit,
  claimCrmCellEditor,
  crmCellKey,
  releaseCrmCellEditor,
  useCrmCellSaving
} from "components/_dashboard/crm/table/cells/crmCellEditing";
import type { ReactNode } from "react";
import type {
  CrmCellSubject,
  CrmEditableDimension
} from "components/_dashboard/crm/table/edit/crmCellEditModel";

/**
 * ONE FIELD OF THE סקירה TAB, EDITABLE IN PLACE — the same popup the CRM table uses.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * What this is, and what it deliberately is NOT
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Requested as: *"I want us to be able to click on '+איזורים', '+הוסף אימייל' and all
 * others, and actually see a small popup to write the value — just like we have in the
 * table … those should be the same popup components … make sure to save on close of the
 * popup … capture the outside click so that any click outside close the popup (and does
 * not trigger something else)."*
 *
 * So this is a HOST, not an editor. It renders `CrmCellEditor` — the real one, with its
 * controls, its draft model, its write path, its failure toast and its dismissal
 * behaviour — and adds only the two things a chip row needs that a table cell does not:
 * a trigger it can anchor to, and a lifecycle it can be unmounted by. Everything the user
 * asked for was already true of that component; none of it is reimplemented here.
 *
 * ── The trigger is the CALLER's, via `render` ────────────────────────────────────
 *
 * The two panels have very different triggers — a dashed `+ אזורים` pill, a filled ✓ chip,
 * a quiet `+ הוסף אימייל…` button, a plain value line — and each is already styled to fit
 * its panel. A `children` slot would have meant this component owning that styling; a
 * render prop lets it own only the anchoring. The caller gets `open` and calls it from
 * whatever it likes.
 *
 * ── The anchor overlay, which is not decoration ──────────────────────────────────
 *
 * `CrmCellEditor`'s default anchor is `width/height: 100%`, which is exactly right inside
 * a table cell and produces a ZERO-HEIGHT box inside a wrapping chip row — the surface
 * then opens at the chip's top edge instead of under it. Worse, the editor sizes itself as
 * `max(controlMinWidth, anchor.offsetWidth)`, so an anchor that measured the whole
 * reference column would open a 352px popover for a 60px chip.
 *
 * A `position: relative` wrapper plus an absolutely-positioned `inset: 0` overlay gives
 * the anchor the trigger's exact rect in both axes. `pointerEvents: "none"` keeps it out
 * of the way of the trigger underneath — safe because the surface is PORTALLED and the
 * dismissal logic tests the Paper, never the anchor.
 *
 * ── One editor at a time, and the click that closes it does nothing else ─────────
 *
 * Both come free, from two different mechanisms already in the codebase:
 *
 *  - `useOutsideDismiss` (inside `CrmCellEditor`) arms a capture-phase swallow on
 *    `pointerdown` and eats the paired `click`. So clicking chip B while chip A is open
 *    commits-and-closes A and does NOT open B — which is the behaviour that was asked for,
 *    stated in those words.
 *  - `claimCrmCellEditor` is the list's single-editor registry, and it is claimed here too.
 *    That is belt to the swallow's braces: it also covers the case where a keyboard or a
 *    programmatic open races an existing one, and it means a panel editor and a TABLE
 *    editor cannot be open together on the CRM page, where both are on screen at once.
 *
 * ── What it does NOT do: optimistic patching ─────────────────────────────────────
 *
 * There is no `onPatched` here, and that is deliberate rather than missing. Both hosts of
 * this tab read the contact from a LIVE Firestore listener (`useLiveContact` on the full
 * page, `useCrmPanelContact`'s own-contact branch in the CRM side panel), so a successful
 * write repaints the chip on its own — and the CRM side panel already projects that same
 * document onto the list row beside it. Adding an optimistic overlay would buy a few
 * hundred milliseconds and cost a value that flashes to the new number and back on a
 * failure; without it a failed save simply never changes the chip, and the editor's own
 * snackbar says why. The honest render is the cheaper one here.
 */

export type InlineFieldEditProps = {
  /** The contact, projected — see `contactEditSubject.ts`. */
  subject: CrmCellSubject;
  dimension: CrmEditableDimension;
  /**
   * When false the trigger renders exactly as it would otherwise but `open` does nothing,
   * so the caller can route elsewhere (today: the full edit dialog, for a record the
   * viewer does not own).
   */
  disabled?: boolean;
  /**
   * How the wrapper participates in ITS parent's layout.
   *
   * ⚠️ THE WRAPPER IS A NEW FLEX CONTAINER, so it intercepts anything the trigger used to
   * negotiate with the panel directly. A chip wants `inline` — shrink-wrapped, so a row of
   * them still wraps as a row of chips. A full-width block (the ✦ panel's expanded rows,
   * a details row's value) wants `block`: without it the trigger's own `alignSelf:
   * "stretch"` now resolves against this wrapper instead of the panel's Stack and silently
   * does nothing, so the hover highlight and the click target shrink to the text and the
   * row stops reading as one target.
   */
  display?: "inline" | "block";
  /**
   * The field's own Hebrew label — `אימייל`, `תקציב`, `אזורים`.
   *
   * Two jobs, and both of them belong to the CALLER because only it knows what the
   * trigger says. It is half of the `crmCellKey` this field is tracked by (so the saving
   * indicator below is per FIELD rather than per contact), and it is what the editor's
   * failure toast names — that toast can land seconds later on a different screen, so
   * "העדכון נכשל" with no subject is unactionable.
   */
  label: string;
  /** Renders the trigger. `open` is a no-op while `disabled`. */
  render: (open: () => void, isOpen: boolean) => ReactNode;
};

export function InlineFieldEdit({
  subject,
  dimension,
  disabled,
  display = "inline",
  label,
  render
}: InlineFieldEditProps): JSX.Element {
  const [isOpen, setOpen] = useState(false);

  /**
   * ── THE FIELD SAYS SO WHILE ITS WRITE IS IN THE AIR ──────────────────────────
   *
   * The editor commits and closes IMMEDIATELY, without waiting for the round trip — that
   * is the fill-then-move-on flow and it is deliberate. The consequence is that for the
   * second the write takes, the thing on screen is this trigger, showing the OLD value
   * (there is no optimistic overlay here — see the note at the foot of this header), with
   * nothing to say a save is pending. That reads as "clicked away and nothing happened",
   * which is the one impression the whole save-on-close model must not give.
   *
   * So the field borrows the table's mechanism rather than growing a second one:
   * `crmCellEditing` is a module registry both halves can reach, the editor marks the key
   * it was handed when the request leaves and clears it when it lands, and this subscribes
   * to that key. Identical machinery, identical semantics, one implementation.
   *
   * ⚠️ THE KEY IS `(contact, label)`, so two fields of the same contact are two keys —
   * otherwise filling אימייל would spin כתובת. The label is the caller's for the same
   * reason it is in the table: the trigger knows what it says, the editor knows only a
   * dimension, and neither can derive the other's half.
   */
  const cellKey = crmCellKey(subject.contactId, label);
  const saving = useCrmCellSaving(cellKey);

  /**
   * A STABLE release identity, because that is what the registry compares on. A fresh
   * closure each render would make every re-render look like a different holder, so the
   * registry would close this editor on the next claim by its own component.
   */
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    // Released on unmount as well as on close: a panel that re-renders the contact away
    // (a switch to another contact, a tab change) would otherwise leave the registry
    // holding a closure into a component that no longer exists.
    if (!isOpen) return undefined;
    claimCrmCellEditor(close);
    return () => releaseCrmCellEditor(close);
  }, [isOpen, close]);

  const open = useCallback(() => {
    if (disabled) return;
    // A field whose value is being written refuses to re-open, exactly as a table cell
    // does: a second editor over an in-flight save is how two writes race on one field.
    if (saving) return;
    // The field names itself for the editor that is about to mount. It MUST run before
    // the editor exists — the editor consumes the announcement once, at mount — and the
    // announcement carries a 1s TTL, so a state flush in between is not a problem.
    announceCrmCellEdit({ key: cellKey, label });
    setOpen(true);
  }, [disabled, saving, cellKey, label]);

  /**
   * The subject at OPEN time, frozen for the life of the editor.
   *
   * `CrmCellEditor` seeds its draft once, on mount, and its own contract says the host
   * must unmount it rather than hand it a new subject. The live listener behind these
   * panels fires on every snapshot — INCLUDING the echo of this editor's own write — so
   * passing the live object straight through would hand a re-rendered subject to a
   * mounted editor and invite exactly the staleness that contract exists to prevent.
   * Frozen here, the editor compares its draft against what the contact held when the
   * realtor opened it, which is what they were looking at.
   */
  const frozenRef = useRef<CrmCellSubject | null>(null);
  if (isOpen && !frozenRef.current) frozenRef.current = subject;
  if (!isOpen && frozenRef.current) frozenRef.current = null;

  return (
    <Box
      /**
       * ⚠️ `block` IS A PLAIN BLOCK, AND THAT IS A BUG FIX, not a preference.
       *
       * It was `display: flex` with `maxWidth: 100%` and `minWidth: 0`, and in the פרטים
       * panel that collapsed the value to a few characters — כתובת rendered "חי / פה" on
       * two lines and a ת״ז broke after seven digits, with two hundred pixels of empty
       * row beside them. The tell was that `בעלים`, which renders the SAME `valueSx`
       * without this wrapper, was fine: the wrapper was the variable.
       *
       * A shrink-to-fit box (inline-flex, or a flex container sized by its content) has to
       * resolve its own width from its contents — and `valueSx` carries
       * `overflowWrap: "anywhere"`, which drops a text run's min-content width to a SINGLE
       * CHARACTER. Any path that lands on that minimum produces exactly what was reported.
       * A block does not have the question to get wrong: it fills its parent, and the text
       * inside wraps at the real edge.
       *
       * The `inline` variant keeps shrink-to-fit because a chip must not span its row —
       * and it no longer carries `minWidth: 0`, which existed only to permit the collapse.
       */
      sx={
        display === "block"
          ? { position: "relative", display: "block", width: "100%", minWidth: 0 }
          : { position: "relative", display: "inline-flex", maxWidth: "100%" }
      }
    >
      {render(open, isOpen)}

      {saving && (
        <Box
          component="span"
          /* Decorative: `aria-busy` on the trigger already announces it, and one live
             region per field would make filling a card read as a stream of announcements. */
          aria-hidden
          sx={{
            position: "absolute",
            // All four offsets are 0, so there is no physical edge for stylis to mirror.
            inset: 0,
            display: "flex",
            alignItems: "center",
            // Logical: the spinner sits at the reading END, out of the way of a value that
            // reads from the start edge.
            justifyContent: "flex-end",
            px: 0.5,
            borderRadius: 1,
            // A VEIL over the value, not a replacement for it — the realtor can still read
            // what they typed while it is written, which is what makes a late failure
            // legible when the value snaps back to the old one.
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.66),
            // The trigger underneath keeps the click so it can swallow it.
            pointerEvents: "none"
          }}
        >
          <CircularProgress size={13} thickness={5.5} />
        </Box>
      )}
      {isOpen && frozenRef.current && (
        <CrmCellEditor
          /* Keyed on the dimension AND the contact, never on a VALUE. A key that moved
             with the data would remount the editor mid-edit on the first snapshot the
             listener delivered, discarding whatever had been typed. */
          key={`${dimension}:${frozenRef.current.contactId}`}
          row={frozenRef.current}
          dimension={dimension}
          anchorSx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          onDone={close}
        />
      )}
    </Box>
  );
}

export default InlineFieldEdit;
